import { useState, useMemo, useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useSearchNotes } from '@/services/noteGoApi/noteGoApiComponents';
import { NoteOut } from '@/services/noteGoApi/noteGoApiSchemas';
import { errorReporter } from '@/services/errorReportingService';
import { VIBE_CODE_SEARCH_DEBOUNCE_MS } from '../config';
import { useDebounce } from './useDebounce';

export const useNoteSearch = () => {
  const { userToken } = useAuthStore();
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedQuery = useDebounce(searchQuery, VIBE_CODE_SEARCH_DEBOUNCE_MS);

  const hasQuery = debouncedQuery.trim().length >= 2;

  const { data, isLoading, error, isError } = useSearchNotes(
    {
      headers: { authorization: `Bearer ${userToken}` },
      queryParams: {
        keywords: debouncedQuery.trim(),
        limit: 10,
        sort_by: 'created_at',
        sort_order: 'desc',
      },
    },
    {
      enabled: hasQuery && !!userToken,
    },
  );

  useEffect(() => {
    if (isError && error) {
      errorReporter.expected('Note search failed', error, {
        feature: 'vibeCode',
        operation: 'searchNotes',
      });
    }
  }, [isError, error]);

  // Filter to only notes that have a video_url (since Gemini needs the video)
  const results: NoteOut[] = useMemo(() => {
    if (!data) return [];
    const notes: NoteOut[] = Array.isArray(data) ? data : ((data as { notes?: NoteOut[] })?.notes ?? []);
    return notes.filter((note) => !!note.video_url);
  }, [data]);

  return {
    searchQuery,
    setSearchQuery,
    results,
    isLoading: hasQuery && isLoading,
    hasQuery,
    isError,
  };
};
